<script lang="ts">
  import { tick, onMount } from 'svelte';
  import { _ } from 'svelte-i18n';
  import { ConfigStore } from '../lib/store/config';
  import TextSwitch from './text-switch.svelte';
  import AudioWave from './audio-wave.svelte';
  import type { TranscriptEntry } from './types';
  import SvgIcon from '../lib/components/svg-icon.svelte';
  import {
    voiceState,
    transcriptVisible,
    startVoice,
    stopVoice,
    voiceControls,
  } from './bridge';

  onMount(() => { void voiceControls.refreshDevices(); });
  function selectDevice(event: Event, kind: 'audioinput' | 'videoinput') {
    const select = event.currentTarget as HTMLSelectElement;
    if (select.value === '__request_access__') {
      select.value = kind === 'audioinput' ? $voiceState.selectedMicrophone : $voiceState.selectedCamera;
      void voiceControls.refreshDevices(kind);
    } else void voiceControls.selectDevice(kind, select.value);
  }
  let transcriptElement: HTMLDivElement;
  let followTranscript = true;
  $: microphones = $voiceState.devices.filter(
    (device) => device.kind === 'audioinput'
  );
  $: cameras = $voiceState.devices.filter(
    (device) => device.kind === 'videoinput'
  );
  let lastTranscript: TranscriptEntry[] | undefined;
  let wasTranscriptVisible = false;
  $: displayStatus = $voiceState.active ? $voiceState.status : 'idle';
  $: followTranscriptUpdates($voiceState.transcript, $transcriptVisible, followTranscript);
  function followTranscriptUpdates(entries: TranscriptEntry[], visible: boolean, follow: boolean) {
    const changed = entries !== lastTranscript || (visible && !wasTranscriptVisible);
    lastTranscript = entries;
    wasTranscriptVisible = visible;
    if (changed && visible && follow) void scrollTranscript();
  }
  async function scrollTranscript() {
    await tick();
    if (transcriptElement)
      transcriptElement.scrollTop = transcriptElement.scrollHeight;
  }

  function preview(
    element: HTMLVideoElement,
    params: { source: 'camera' | 'screen'; revision: number }
  ) {
    let detach = voiceControls.attachPreview(element, params.source);
    return {
      update(next: typeof params) {
        if (next.source === params.source && next.revision === params.revision)
          return;
        params = next;
        detach?.();
        detach = voiceControls.attachPreview(element, next.source);
      },
      destroy() {
        detach?.();
      },
    };
  }
</script>

<section class="cs-voice-panel" aria-label={$_('voice.voice_mode')}>
  <div class="cs-voice-main">
    {#if $voiceState.error}
      <p class="cs-voice-error" role="alert">
        {$_(`voice.${$voiceState.error}`)}
      </p>
    {/if}
    {#if $voiceState.audioBlocked}
      <button
        class="cs-voice-audio"
        on:click={() => voiceControls.startAudio()}
        title={$_('voice.enable_audio')}
        aria-label={$_('voice.enable_audio')}
      >
        <SvgIcon name="volume-2" />
      </button>
      <p class="cs-voice-hint">{$_('voice.audio_blocked')}</p>
    {/if}
    <div
      class="cs-voice-presence"
      class:compact={$transcriptVisible ||
        ($voiceState.connected && ($voiceState.camera || $voiceState.screen))}
    >
      <AudioWave readLevel={voiceControls.readAudioLevel} active={$voiceState.active}
        speaking={displayStatus === 'speaking'}
        compact={$transcriptVisible || ($voiceState.connected && ($voiceState.camera || $voiceState.screen))} />
      {#key displayStatus}
        <!-- Translation extensions replace text nodes; rebuild on state changes. -->
        <p class="cs-voice-status notranslate" role="status" translate="no">
          {$_(`voice.${displayStatus}`)}
        </p>
      {/key}
      <button
        class="cs-voice-call"
        class:stop={$voiceState.active}
        disabled={!$voiceState.active && $voiceState.busy.length > 0}
        title={$_($voiceState.active ? 'voice.stop_call' : 'voice.start_call')}
        aria-label={$_(
          $voiceState.active ? 'voice.stop_call' : 'voice.start_call'
        )}
        on:click={() => ($voiceState.active ? stopVoice() : startVoice())}
      >
        <SvgIcon name={$voiceState.active ? 'square' : 'play'} />
      </button>
      {#if !$transcriptVisible}<TextSwitch />{/if}
    </div>
    {#if $voiceState.connected && ($voiceState.camera || $voiceState.screen)}
      <div class="cs-voice-previews">
        {#if $voiceState.camera}
          <video
            class="camera"
            muted
            autoplay
            playsinline
            aria-label={$_('voice.camera_preview')}
            use:preview={{
              source: 'camera',
              revision: $voiceState.mediaRevision,
            }}
          ></video>
        {/if}
        {#if $voiceState.screen}
          <video
            muted
            autoplay
            playsinline
            aria-label={$_('voice.screen_preview')}
            use:preview={{
              source: 'screen',
              revision: $voiceState.mediaRevision,
            }}
          ></video>
        {/if}
      </div>
    {/if}
    {#if $transcriptVisible}
      <!-- svelte-ignore a11y_no_noninteractive_tabindex (Scrollable transcript supports keyboard navigation.) -->
      <div
        class="cs-voice-transcript"
        bind:this={transcriptElement}
        role="region"
        tabindex="0"
        aria-label={$_('voice.transcript')}
        aria-live="off"
        on:scroll={() => {
          followTranscript =
            transcriptElement.scrollHeight -
              transcriptElement.scrollTop -
              transcriptElement.clientHeight <
            50;
        }}
      >
        {#each $voiceState.transcript as entry (entry.id)}
          <div class="cs-voice-message" class:user={entry.speaker === 'user'}>
            <strong
              >{entry.speaker === 'user'
                ? $_('voice.you')
                : $ConfigStore.agentName}</strong
            >
            <p class:interim={!entry.final}>{entry.text}</p>
          </div>
        {:else}
          <p class="cs-voice-hint">{$_('voice.transcript_empty')}</p>
        {/each}
      </div>
      <TextSwitch />
    {/if}
  </div>
  <div
    class="cs-voice-controls"
    role="group"
    aria-label={$_('voice.media_controls')}
  >
    <div class="cs-voice-device">
      <button
        on:click={() => voiceControls.toggle('microphone')}
        disabled={($voiceState.active
          ? !$voiceState.connected || !$voiceState.canMicrophone
          : !navigator.mediaDevices?.getUserMedia) ||
          $voiceState.busy.includes('microphone')}
        aria-pressed={$voiceState.microphone}
        aria-label={$_($voiceState.microphone ? 'voice.mute' : 'voice.unmute')}
        title={$_(
          !$voiceState.canMicrophone && $voiceState.connected
            ? 'voice.microphone_unavailable'
            : $voiceState.microphone
              ? 'voice.mute'
              : 'voice.unmute'
        )}
      >
        <SvgIcon name={$voiceState.microphone ? 'mic' : 'mic-off'} />
      </button>
      <select
        aria-label={$_('voice.microphone_device')}
        title={$_('voice.microphone_device')}
        disabled={($voiceState.active
          ? !$voiceState.connected || !$voiceState.canMicrophone
          : !navigator.mediaDevices?.getUserMedia) ||
          $voiceState.busy.includes('microphone')}
        value={$voiceState.selectedMicrophone}
        on:focus={() => voiceControls.refreshDevices()}
        on:change={(event) => selectDevice(event, 'audioinput')}
      >
        <option value="">{$_('voice.default_device')}</option>
        {#if !microphones.some((device) => device.label)}
          <option value="__request_access__">{$_('voice.allow_microphone_devices')}</option>
        {/if}
        {#each microphones.filter((device) => device.deviceId) as device, i}<option value={device.deviceId}
            >{device.label ||
              `${$_('voice.microphone_device')} ${i + 1}`}</option
          >{/each}
      </select>
    </div>
    {#if $ConfigStore.widget?.live?.supportsVideoInput}
      <div class="cs-voice-device">
        <button
          on:click={() => voiceControls.toggle('camera')}
          disabled={($voiceState.active
            ? !$voiceState.connected || !$voiceState.canCamera
            : !navigator.mediaDevices?.getUserMedia) ||
            $voiceState.busy.includes('camera')}
          aria-pressed={$voiceState.camera}
          aria-label={$_(
            $voiceState.camera ? 'voice.camera_off' : 'voice.camera_on'
          )}
          title={$_(
            !$voiceState.canCamera && $voiceState.connected
              ? 'voice.media_unavailable'
              : $voiceState.camera
                ? 'voice.camera_off'
                : 'voice.camera_on'
          )}
        >
          <SvgIcon name={$voiceState.camera ? 'video' : 'video-off'} />
        </button>
        <select
          aria-label={$_('voice.camera_device')}
          title={$_('voice.camera_device')}
          disabled={($voiceState.active
            ? !$voiceState.connected || !$voiceState.canCamera
            : !navigator.mediaDevices?.getUserMedia) ||
            $voiceState.busy.includes('camera')}
          value={$voiceState.selectedCamera}
          on:focus={() => voiceControls.refreshDevices()}
          on:change={(event) => selectDevice(event, 'videoinput')}
        >
          <option value="">{$_('voice.default_device')}</option>
          {#if !cameras.some((device) => device.label)}
            <option value="__request_access__">{$_('voice.allow_camera_devices')}</option>
          {/if}
          {#each cameras.filter((device) => device.deviceId) as device, i}<option value={device.deviceId}
              >{device.label || `${$_('voice.camera_device')} ${i + 1}`}</option
            >{/each}
        </select>
      </div>
    {/if}
    {#if $ConfigStore.widget?.live?.supportsScreenShare}
      <button
        on:click={() => voiceControls.toggle('screen')}
        disabled={!$voiceState.connected ||
          !$voiceState.canScreen ||
          $voiceState.busy.includes('screen')}
        aria-pressed={$voiceState.screen}
        aria-label={$_(
          $voiceState.screen ? 'voice.screen_off' : 'voice.screen_on'
        )}
        title={$_(
          !$voiceState.canScreen && $voiceState.connected
            ? 'voice.media_unavailable'
            : $voiceState.screen
              ? 'voice.screen_off'
              : 'voice.screen_on'
        )}
      >
        <SvgIcon
          name={$voiceState.screen ? 'screen-share-off' : 'screen-share'}
        />
      </button>
    {/if}
    <button
      aria-pressed={$transcriptVisible}
      aria-label={$_('voice.transcript')}
      title={$_('voice.transcript')}
      on:click={() => {
        transcriptVisible.set(!$transcriptVisible);
        followTranscript = true;
      }}><SvgIcon name="captions" /></button
    >
  </div>
  {#if $ConfigStore.showBranding}
    <div class="cs-voice-branding">
      Powered by <a
        href="https://www.conversagent.app/"
        target="_blank"
        rel="noreferrer"
        ><img
          alt="Conversagent"
          src="https://components.clevertar.app/gqa/conversagent-logo.png"
        /> Conversagent</a
      >
    </div>
  {/if}
</section>

<style>
  .cs-voice-panel {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
    overflow: hidden;
    color: var(--cs-color, #444);
    font: inherit;
  }
  .cs-voice-main {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
    overflow: auto;
    padding: 12px 16px;
    gap: 12px;
  }
  .cs-voice-presence {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 185px;
    gap: 18px;
  }
  .cs-voice-presence.compact {
    flex: 0;
    min-height: 130px;
    gap: 8px;
  }
  .cs-voice-status {
    margin: 0;
    font-size: 16px;
    font-weight: 600;
    text-align: center;
  }
  button {
    font: inherit;
    border: 1px solid #8885;
    background: transparent;
    color: inherit;
    border-radius: 10px;
    width: 44px;
    height: 44px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    font-size: 23px;
    flex-shrink: 0;
  }
  button:disabled,
  select:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
  button:hover:not(:disabled) {
    background: #8882;
  }
  button:focus-visible,
  select:focus-visible {
    outline: 2px solid var(--cs-bg-button-color, #279ac0);
    outline-offset: 2px;
  }
  button[aria-pressed='true'] {
    color: var(--cs-bg-button-color, #279ac0);
  }
  .cs-voice-controls > button[aria-pressed='true'] {
    background: #8881;
  }
  .cs-voice-call {
    background: var(--cs-bg-button-color, #279ac0);
    color: white;
    width: 56px;
    height: 48px;
    border: 0;
  }
  .cs-voice-call.stop {
    background: #b83240;
  }
  .cs-voice-call:hover:not(:disabled) {
    filter: brightness(0.9);
    background: var(--cs-bg-button-color, #279ac0);
  }
  .cs-voice-call.stop:hover:not(:disabled) {
    background: #b83240;
  }
  .cs-voice-call :global(svg) {
    fill: currentColor;
    width: 19px;
  }
  .cs-voice-controls {
    display: flex;
    align-items: center;
    justify-content: space-evenly;
    gap: 6px;
    flex-wrap: wrap;
    padding: 10px 6px;
    border-top: 1px solid #8884;
    flex-shrink: 0;
  }
  .cs-voice-device {
    display: flex;
    align-items: center;
    border: 1px solid #8885;
    border-radius: 10px;
  }
  .cs-voice-device button {
    border: 0;
    border-radius: 9px 0 0 9px;
  }
  select {
    width: 24px;
    height: 40px;
    color: inherit;
    background: transparent;
    border: 0;
    border-left: 1px solid #8884;
    cursor: pointer;
    font-size: 0;
  }
  select option {
    font-size: 14px;
    color: #222;
    background: white;
  }
  .cs-voice-previews {
    display: flex;
    gap: 8px;
    justify-content: center;
    flex-wrap: wrap;
    flex-shrink: 0;
  }
  video {
    width: min(100%, 180px);
    max-height: 115px;
    background: #151515;
    border-radius: 8px;
    object-fit: contain;
  }
  video.camera {
    transform: scaleX(-1);
  }
  .cs-voice-transcript {
    flex: 1;
    min-height: 100px;
    overflow: auto;
    padding: 2px;
  }
  .cs-voice-transcript,
  .cs-voice-main {
    scrollbar-gutter: stable;
  }
  .cs-voice-transcript::-webkit-scrollbar,
  .cs-voice-main::-webkit-scrollbar { width: 2px; }
  .cs-voice-transcript::-webkit-scrollbar-track,
  .cs-voice-main::-webkit-scrollbar-track { background: transparent; }
  .cs-voice-transcript::-webkit-scrollbar-thumb,
  .cs-voice-main::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 10px;
    min-height: 20px;
  }
  .cs-voice-transcript:focus-visible {
    outline: 2px solid var(--cs-bg-button-color, #7123c0);
  }
  .cs-voice-message {
    margin: 8px 0;
    padding: 10px;
    border-radius: 15px;
    border: 1px solid #ccc;
    background: var(--cs-bg-a-color, #f0f0f0);
    color: var(--cs-text-a-color, #000);
    font-size: 14px;
  }
  .cs-voice-message.user {
    margin-left: 24px;
    background: var(--cs-bg-q-color, #7123c0);
    color: var(--cs-text-q-color, #fff);
  }
  .cs-voice-message strong {
    font-size: 12px;
  }
  .cs-voice-message p {
    margin: 5px 0 0;
    white-space: pre-wrap;
    overflow-wrap: anywhere;
    line-height: 1.45;
  }
  .interim {
    opacity: 0.7;
  }
  .cs-voice-hint {
    font-size: 13px;
    text-align: center;
    margin: 0;
    opacity: 0.8;
  }
  .cs-voice-error {
    margin: 0;
    background: #b8324012;
    border: 1px solid #b8324050;
    border-radius: 8px;
    padding: 10px;
    font-size: 13px;
    line-height: 1.5;
  }
  .cs-voice-audio {
    align-self: center;
  }
  .cs-voice-branding {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 4px;
    font-size: 10px;
  }
  .cs-voice-branding a {
    display: inline-flex;
    align-items: center;
    color: inherit;
  }
  .cs-voice-branding img {
    height: 15px;
    margin: 0 5px;
  }
</style>
